import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Pill } from "@/components/perf";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Check, X, Phone, MapPin, Clock, MessageSquare, DollarSign, Users } from "lucide-react";
import LoungeProfileForm from "@/components/LoungeProfileForm";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromIso = (s) => new Date(s + "T00:00:00");
const fmtDate = (d) => fromIso(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

const STATUS_STYLE = {
  pending: { color: "#FACC15", label: "Pending" },
  confirmed: { color: "#4ADE80", label: "Confirmed" },
  declined: { color: "#F87171", label: "Declined" },
};

function PayBadge({ a }) {
  if (a.payment_status === "paid") return <Pill color="#4ADE80">Paid · ${a.amount}</Pill>;
  if (a.payment_status === "due_in_person") return <Pill color="#FACC15">${a.amount} in person</Pill>;
  return <Pill color="#71717A">Free</Pill>;
}

function AppointmentCard({ a, onApprove, onDecline, busy, showCoach }) {
  const st = STATUS_STYLE[a.status] || STATUS_STYLE.confirmed;
  return (
    <div data-testid={`appointment-${a.id}`} className="border border-pb-border bg-pb-elevated p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-semibold flex items-center gap-2 flex-wrap">
            {a.client_name}
            <Pill color={st.color}>{st.label}</Pill>
            <PayBadge a={a} />
            {a.needs_refund && <Pill color="#F87171">Refund needed</Pill>}
          </div>
          <div className="text-xs text-zinc-400 mt-1.5 space-y-1">
            <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {fmtDate(a.date)} · {a.time} · {a.drill_type}</div>
            <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {a.location}{showCoach ? ` · Coach ${a.coach}` : ""}</div>
            <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {a.phone} (prefers {a.contact_method})</div>
            {a.note && <div className="flex items-center gap-1.5 text-zinc-500"><MessageSquare className="w-3 h-3" /> "{a.note}"</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {a.status !== "confirmed" && (
            <button data-testid={`approve-${a.id}`} onClick={() => onApprove(a)} disabled={busy}
              className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-3 py-2 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
          )}
          {a.status !== "declined" && (
            <button data-testid={`decline-${a.id}`} onClick={() => onDecline(a)} disabled={busy}
              className="flex items-center gap-1.5 border border-red-500/60 text-red-400 font-bold uppercase tracking-widest text-xs px-3 py-2 hover:bg-red-500 hover:text-black active:scale-95 transition-all disabled:opacity-50">
              <X className="w-3.5 h-3.5" /> Decline
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CoachesCorner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/corner"); setData(data); setError(null); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const slotsByDate = useMemo(() => {
    const m = {};
    (data?.slots || []).forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    return m;
  }, [data]);
  const apptsBySlot = useMemo(() => {
    const m = {};
    (data?.appointments || []).forEach((a) => { (m[a.slot_id] = m[a.slot_id] || []).push(a); });
    return m;
  }, [data]);

  if (user && user.role !== "admin" && !user.is_coach) return <Navigate to="/profile" replace />;

  const approve = async (a) => {
    setBusyId(a.id);
    try { await api.post(`/coach/appointments/${a.id}/approve`); toast.success(`${a.client_name}'s session approved`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const decline = async (a) => {
    if (!window.confirm(`Decline ${a.client_name}'s appointment?${a.payment_status === "paid" ? " They paid via Stripe — a manual refund will be needed." : ""}`)) return;
    setBusyId(a.id);
    try {
      const { data: r } = await api.post(`/coach/appointments/${a.id}/decline`);
      toast.success(r.needs_refund ? "Declined — refund flagged for staff" : "Appointment declined");
      await load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const shift = (dir) => {
    const d = new Date(anchor);
    if (view === "day") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir);
    setAnchor(d);
  };

  const weekDays = () => {
    const start = new Date(anchor);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  };
  const monthCells = () => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  };

  const todayIso = iso(new Date());
  const pending = (data?.appointments || []).filter((a) => a.status === "pending");
  const upcoming = (data?.appointments || []).filter((a) => a.date >= todayIso);

  const headerLabel = view === "month"
    ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : view === "week"
      ? `Week of ${weekDays()[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  const DayCell = ({ d, compact }) => {
    const key = iso(d);
    const daySlots = slotsByDate[key] || [];
    const isToday = key === todayIso;
    const inMonth = d.getMonth() === anchor.getMonth();
    return (
      <button data-testid={`cal-day-${key}`} onClick={() => { setAnchor(new Date(d)); setView("day"); }}
        className={`text-left border p-2 min-h-[72px] transition-colors hover:border-pb-red ${isToday ? "border-pb-cyan bg-pb-cyan/5" : "border-pb-border/60"} ${compact && !inMonth ? "opacity-30" : ""}`}>
        <div className={`text-xs font-bold ${isToday ? "text-pb-cyan" : "text-zinc-400"}`}>{d.getDate()}</div>
        <div className="mt-1 space-y-1">
          {daySlots.slice(0, compact ? 2 : 4).map((s) => {
            const pend = (apptsBySlot[s.id] || []).some((a) => a.status === "pending");
            return (
              <div key={s.id} className={`text-[10px] px-1 py-0.5 truncate ${pend ? "bg-yellow-500/20 text-yellow-300" : "bg-pb-red/15 text-[#BEF264]"}`}>
                {s.time} {s.drill_type}
              </div>
            );
          })}
          {daySlots.length > (compact ? 2 : 4) && <div className="text-[10px] text-zinc-500">+{daySlots.length - (compact ? 2 : 4)} more</div>}
        </div>
      </button>
    );
  };

  return (
    <div data-testid="coaches-corner-page">
      <div className="relative overflow-hidden border border-pb-border bg-pb-surface mb-8">
        <div className="absolute inset-0 bg-gradient-to-r from-pb-base via-pb-base/70 to-transparent" />
        <div className="relative px-5 py-8 md:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-pb-cyan mb-2">Coach Portal · {data?.coach_name || "…"}</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">Coaches Corner</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl text-sm md:text-base">Your schedule and client appointments. Approve or decline booking requests — clients are notified instantly.</p>
        </div>
      </div>

      {error && <div className="border border-red-500/50 bg-red-500/10 text-red-300 p-5 mb-8 text-sm" data-testid="coach-corner-error">{error}</div>}

      <LoungeProfileForm />

      {pending.length > 0 && (
        <div className="bg-pb-surface border border-yellow-500/40 p-5 mb-8" data-testid="pending-approvals">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-yellow-400" />
            <span className="font-display font-bold text-xl uppercase">Pending Approvals</span>
            <Pill color="#FACC15">{pending.length}</Pill>
          </div>
          <div className="space-y-3">
            {pending.map((a) => <AppointmentCard key={a.id} a={a} onApprove={approve} onDecline={decline} busy={busyId === a.id} showCoach={data?.is_admin} />)}
          </div>
        </div>
      )}

      <div className="bg-pb-surface border border-pb-border mb-8" data-testid="coach-calendar">
        <div className="flex items-center justify-between flex-wrap gap-3 p-5 border-b border-pb-border">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-pb-red" />
            <span className="font-display font-bold text-xl uppercase">{headerLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-pb-border">
              {["day", "week", "month"].map((v) => (
                <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${view === v ? "bg-pb-red text-black" : "text-zinc-400 hover:text-white"}`}>
                  {v}
                </button>
              ))}
            </div>
            <button data-testid="cal-prev" onClick={() => shift(-1)} className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-red"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="cal-today" onClick={() => setAnchor(new Date())} className="px-3 h-8 text-xs font-bold uppercase tracking-widest border border-pb-border hover:border-pb-red">Today</button>
            <button data-testid="cal-next" onClick={() => shift(1)} className="w-8 h-8 flex items-center justify-center border border-pb-border hover:border-pb-red"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="p-4">
          {view !== "day" && (
            <div className="grid grid-cols-7 gap-1 mb-1">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="text-center text-[10px] uppercase tracking-widest text-zinc-500 py-1">{d}</div>
              ))}
            </div>
          )}
          {view === "month" && <div className="grid grid-cols-7 gap-1">{monthCells().map((d) => <DayCell key={iso(d)} d={d} compact />)}</div>}
          {view === "week" && <div className="grid grid-cols-7 gap-1">{weekDays().map((d) => <DayCell key={iso(d)} d={d} />)}</div>}
          {view === "day" && (
            <div className="space-y-4" data-testid="day-view">
              {(slotsByDate[iso(anchor)] || []).length === 0 && <div className="text-sm text-zinc-500 py-6 text-center">No sessions scheduled this day.</div>}
              {(slotsByDate[iso(anchor)] || []).map((s) => (
                <div key={s.id} className="border border-pb-border p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {s.time} <Pill color="#A3E635">{s.drill_type}</Pill>
                      {s.price > 0 ? <Pill color="#22D3EE">${s.price}</Pill> : <Pill color="#71717A">Free</Pill>}
                    </div>
                    <span className="text-xs text-zinc-500 font-mono flex items-center gap-2">
                      <MapPin className="w-3 h-3" /> {s.location} · {s.booked}/{s.capacity} booked{data?.is_admin ? ` · ${s.coach}` : ""}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(apptsBySlot[s.id] || []).length === 0 && <div className="text-xs text-zinc-600">No bookings yet.</div>}
                    {(apptsBySlot[s.id] || []).map((a) => <AppointmentCard key={a.id} a={a} onApprove={approve} onDecline={decline} busy={busyId === a.id} showCoach={data?.is_admin} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-pb-surface border border-pb-border p-5" data-testid="all-appointments">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-pb-red" />
          <span className="font-display font-bold text-xl uppercase">Upcoming Appointments</span>
          <Pill color="#A3E635">{upcoming.length}</Pill>
        </div>
        <div className="space-y-3">
          {upcoming.length === 0 && <div className="text-sm text-zinc-500">No upcoming appointments.</div>}
          {upcoming.map((a) => <AppointmentCard key={a.id} a={a} onApprove={approve} onDecline={decline} busy={busyId === a.id} showCoach={data?.is_admin} />)}
        </div>
      </div>
    </div>
  );
}
